import React, { useState, useRef, useEffect } from "react";

import { useParams, useHistory } from "react-router-dom";
import LoadingPage from "shared/LoadingPage";

import { useIncident } from "./useIncident";
import { useIncidents } from "../useIncidents";
import { useIBOInfo } from "shared/@hooks/useIBOInfo";
import { useSuspendUser } from "Security/EscalationsPage/useEscalations";

import styles from "./IncidentPage.module.css";

import CaseInfoCard from "../CaseInfoCard";
import IBOInfoCard from "../IBOInfoCard";
import HistoryCard from "../HistoryCard";
import { TabContainer, Tab } from "shared/TabContainer";
import { Select } from "shared/Select/Select";

import CalendarEventDates from "../../CalendarPage/CalendarEventDates";
import IncidentDocumentation from "../IncidentDocumentation";
import CreateNote from "../CreateNote";
import IncidentCommunication from "../IncidentCommunication";
import RelatedIncidentsList from "../RelatedIncidentsList";
import IncidentDetails from "../IncidentDetails";
import SuspensionBanner from "Security/Shared/SuspensionBanner";

import EditIncidentInfoModal from "../EditIncidentInfoModal";
import CreateNewReminderModal from "../CreateNewReminderModal";
import EscalateCaseModal from "../EscalateCaseModal";
import SuspendUserModal from "../SuspendUserModal";
import CreateEventModal from "../CreateEventModal";
import IncidentCommunicationModal from "../IncidentCommunicationModal";
import IncidentUpcomingEvent from "../IncidentUpcomingEvent";

import dayjs from "dayjs";
import { nanoid } from "nanoid";

const testEventsDate = [
  {
    date: "08 August",
    time: "All Day",
    location: "Phoenix, Arizona",
    name: "Name of calendar event",
    notes:
      "Notes: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    src: "src",
  },
  {
    date: "18 August",
    time: "All Day",
    location: "Phoenix, Arizona",
    name: "Phoenix calendar event",
    notes:
      "Notes: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    src: "src",
  },
  {
    date: "20 August",
    time: "All Day",
    location: "Phoenix, Arizona",
    name: "Phoenix calendar event",
    notes:
      "Notes: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    src: "src",
  },

  {
    date: "09 September",
    time: "2-2:45 MST",
    location: "Spokane, Washington",
    name: "Spokane calendar event",
    notes:
      "Notes: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    src: "src",
  },

  {
    date: "09 October",
    time: "2-2:45 MST",
    location: "Spokane, Washington",
    name: "Spokane calendar event",
    notes:
      "Notes: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    src: "src",
  },
  {
    date: "22 October",
    time: "2-2:45 MST",
    location: "Spokane, Washington",
    name: "Spokane calendar event",
    notes:
      "Notes: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    src: "src",
  },
];

export function IncidentPage() {
  const id = nanoid();
  const suspendUser = useSuspendUser();
  const [iboData, setIboData] = useState(null);
  const [iboNum, setIboNum] = useState(null);
  const [incident, setIncident] = useState(null);
  const [incidentDetails, setIncidentDetails] = useState(null);
  const [complaintIboNumber, setComplaintIboNumber] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);

  const incidentTemplate = {
    case: {
      status: "",
      problemType: "",
      owner: "",
      createdAt: "",
      severity: "",
      pendingSuspension: [],
    },
    victim: {
      name: "",
      number: "",
      email: "",
      phoneNumber: "",
      username: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      membershipStatus: "",
    },
    aggressor: {
      firstName: "",
      lastName: "",
      iboNumber: "",
      email: "",
      phoneNumber: "",
      username: "",
      membershipStatus: "",
      problemType: "",
      problemLocation: "",
      subLocation: "",
      startDate: "",
      hasViolated: false,
      attemptedResolution: false,
      actionsTakenToResolve: "",
      fromUpline: false,
    },
    documentation: {
      description: "",
      evidence: [],
    },
    notes: [],
    communications: [],
    events: [],
    history: [],
    related: [],
  };

  const [incidentInfo, setIncidentInfo] = useState(incidentTemplate);

  let { incidentNumber } = useParams();
  const {
    incidentData,
    incidentNotePost,
    incidentCommunicationPost,
    incidentEscalatePut,
    incidentEditPut,
    incidentReminderPut,
    incidentDocumentationPut,
    isLoading: isIncidentLoading,
    isFetching: isIncidentFetching,
    downloadImage,
    refetch,
  } = useIncident(incidentNumber);
  const {
    data,
    isLoading: isIncidentsLoading,
    refetch: refetchIncidents,
  } = useIncidents("incidents");
  const { iboInfo, isLoading, refreshIBOInfo: refetchIBO } = useIBOInfo(iboNum);
  const {
    iboInfo: complaintIbo,
    isLoading: complaintIsLoading,
    refreshIBOInfo: refetchComplaint,
  } = useIBOInfo(complaintIboNumber);

  useEffect(() => {
    incidentData && setIncident(incidentData);
  }, [incidentData]);

  useEffect(() => {
    data &&
      setIncidentDetails(
        data.find((incident) => incident._id === incidentNumber)
      );
  }, [data, incidentNumber]);

  // find ibo number
  useEffect(() => {
    if (incidentDetails) {
      setIboNum(incidentDetails.primaryIboNumber);
      setComplaintIboNumber(incidentDetails.complaintAgainst.iboNumber);
    }
  }, [incidentDetails, incidentNumber]);

  useEffect(() => {
    iboInfo && setIboData(iboInfo);
  }, [iboInfo]);

  const [editModalOpen, setEditModalOpen] = useState();
  const [escalateModalOpen, setEscalateModalOpen] = useState();
  const [suspendModalOpen, setSuspendModalOpen] = useState();
  const [reminderModalOpen, setReminderModalOpen] = useState();
  const [createEventModalOpen, setCreateEventModalOpen] = useState();

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [emailContents, setEmailContents] = useState("");

  function handleCreateEvent(event) {
    let _event = { ...event, caseNum: incidentNumber };

    const modalDate = eventModalRef.current.querySelector(
      "[aria-label='Date Select'] > div span"
    ).textContent;

    // Currently no validation for if date is past or future date
    if (dayjs.isDayjs(selectedDate)) {
      if (modalDate === selectedDate.format("MMMM D, YYYY")) {
        _event.date = selectedDate.format("MMMM D, YYYY");
      }
    }
    alert(JSON.stringify(_event));
    setCreateEventModalOpen(false);
  }

  let history = useHistory();
  function handleActionsSelect(option) {
    switch (option) {
      case "Edit Case Info":
        setEditModalOpen(true);
        break;
      case "Escalate":
        setEscalateModalOpen(true);
        break;
      case "Suspend User":
        setSuspendModalOpen(true);
        break;
      case "Export Report":
        history.push({
          pathname: "/security/report/" + incidentNumber,
          state: incidentInfo,
        });
        break;
      case "Add Reminder":
        setReminderModalOpen(true);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    setIncidentInfo({
      // case info
      case: {
        status: incident && incident.status ? incident.status : "N/A",
        problemType:
          incident && incident.problemType ? incident.problemType : "N/A",
        owner: incident && incident.caseOwner ? incident.caseOwner : "N/A",
        createdAt:
          incident && incident.createdAt
            ? dayjs(incident.createdAt).format("MM/DD/YYYY")
            : "N/A",
        severity: incident && incident.severity ? incident.severity : "N/A",
        incidentNumber: incidentNumber,
        pendingSuspension:
          incident &&
          incident.pendingSuspension &&
          incident.pendingSuspension.platforms &&
          incident.pendingSuspension.platforms.length > 0
            ? incident.pendingSuspension
            : "",
      },
      // victim details
      victim: {
        name: incident
          ? `${incident.firstName && incident.firstName} ${
              incident.lastName && incident.lastName
            }`
          : "N/A",
        number: isLoading
          ? "Loading"
          : iboData && iboData.primaryInfo && iboData.primaryInfo.iboNumber
          ? iboData.primaryInfo.iboNumber
          : "N/A",
        email: isLoading
          ? "Loading"
          : incident && incident.primaryEmail
          ? incident.primaryEmail
          : "N/A",
        phoneNumber:
          incident && incident.phoneNumber ? incident.phoneNumber : "N/A",
        username: isLoading
          ? "Loading"
          : iboData && iboData.primaryInfo && iboData.primaryInfo.username
          ? iboData.primaryInfo.username
          : "N/A",
        address: incident && incident.address ? incident.address : "N/A",
        city: incident && incident.city ? incident.city : "N/A",
        state: incident && incident.state ? incident.state : "N/A",
        zip: incident && incident.zipCode ? incident.zipCode : "N/A",
        membershipStatus: isLoading
          ? "Loading"
          : iboData &&
            iboData.membership &&
            iboData.membership.status &&
            iboData.membership.status === "Expired"
          ? "Expired"
          : iboData &&
            iboData.membership &&
            iboData.membership.currentMembership
          ? iboData.membership.currentMembership
          : "N/A",
      },

      // aggressor details
      aggressor: {
        firstName:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.firstName
            ? incident.complaintAgainst.firstName
            : "",
        lastName:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.lastName
            ? incident.complaintAgainst.lastName
            : "",
        iboNumber:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.iboNumber
            ? incident.complaintAgainst.iboNumber
            : "N/A",
        email:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.email
            ? incident.complaintAgainst.email
            : "N/A",
        phoneNumber:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.phoneNumber
            ? incident.complaintAgainst.phoneNumber
            : "N/A",
        username:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.username
            ? incident.complaintAgainst.username
            : "N/A",
        address:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.address
            ? incident.complaintAgainst.address
            : "N/A",
        city:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.city
            ? incident.complaintAgainst.city
            : "N/A",
        state:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.state
            ? incident.complaintAgainst.state
            : "N/A",
        zip:
          incident &&
          incident.complaintAgainst &&
          incident.complaintAgainst.zipCode
            ? incident.complaintAgainst.zipCode
            : "N/A",
        membershipStatus: complaintIsLoading
          ? "Loading"
          : complaintIbo &&
            complaintIbo.membership &&
            complaintIbo.membership.status &&
            complaintIbo.membership.status === "Expired"
          ? "Expired"
          : complaintIbo &&
            complaintIbo.membership &&
            complaintIbo.membership.currentMembership
          ? complaintIbo.membership.currentMembership
          : "N/A",
        problemType:
          incident && incident.problemType ? incident.problemType : "N/A",
        problemLocation:
          incident && incident.problemLocation
            ? incident.problemLocation
            : "N/A",
        subLocation:
          incident && incident.subLocation ? incident.subLocation : "N/A",
        startDate: incident && incident.startDate ? incident.startDate : "N/A",
        hasViolated:
          incident && incident.hasViolated && incident.hasViolated === "Yes"
            ? true
            : false,
        attemptedResolution: incident && incident.escalation,
        actionsTakenToResolve:
          incident && incident.escalation && incident.escalation.actionsNeeded
            ? incident.escalation.actionsNeeded
            : "N/A",
        fromUpline:
          incident && incident.fromUpline && incident.fromUpline === "Yes"
            ? true
            : false,
      },

      // documentation
      documentation: {
        description:
          incident &&
          incident.documentation &&
          incident.documentation.description
            ? incident.documentation.description
            : incident && incident.description
            ? incident.description
            : "",
        evidence:
          incident &&
          incident.documentation &&
          incident.documentation.evidence &&
          incident.documentation.evidence.length > 0
            ? incident.documentation.evidence
                .map((e) => {
                  return {
                    src: e.src,
                    name: e.name,
                    downloadPath: e.downloadPath,
                  };
                })
                .concat(
                  incident.evidence && incident.evidence.length > 0
                    ? incident.evidence.map((e) => {
                        return {
                          src: e.src,
                          name: e.name,
                          downloadPath: e.downloadPath,
                        };
                      })
                    : []
                )
            : incident && incident.evidence && incident.evidence.length > 0
            ? incident.evidence.map((e) => {
                return {
                  src: e.src,
                  name: e.name,
                  downloadPath: e.downloadPath,
                };
              })
            : [],
      },

      // notes
      notes:
        incident && incident.incidentNotes && incident.incidentNotes.length > 0
          ? incident.incidentNotes.map((note) => {
              return {
                date: note.createdAt
                  ? dayjs(note.createdAt).format("MM/DD/YYYY")
                  : "",
                name: note.title ? note.title : "",
                description: note.text ? note.text : "",
                files: note.files && note.files.length > 0 ? note.files : [],
              };
            })
          : [],

      // communications
      communications:
        incident &&
        incident.recentActivity &&
        incident.recentActivity.length > 0
          ? incident.recentActivity.map((activity) => {
              const emailSubject = activity.message
                .toLowerCase()
                .includes("suspended")
                ? incident.pendingSuspension.subject
                : activity.emailSubject;
              const emailBody = activity.message
                .toLowerCase()
                .includes("suspended")
                ? incident.pendingSuspension.emailBody
                : activity.emailBody;

              return {
                date: dayjs(activity.date).format("MM/DD/YYYY"),
                message: activity.message,
                emailSubject: emailSubject,
                emailBody: emailBody,
              };
            })
          : [],
      // events
      events:
        incident && incident.events && incident.events.length > 0
          ? incident.events.map((event) => {
              return {
                name: event.title,
                location: event.location,
                date: dayjs(event.timestamp).format("MM/DD/YYYY"),
                time: dayjs(event.timestamp).format("hh:mm A"),
                notes: event.notes,
                src: "src", // no image returned by API
              };
            })
          : [],

      // history
      history:
        incident &&
        incident.historyOfActions &&
        incident.historyOfActions.length > 0
          ? incident.historyOfActions.map((action) => {
              return {
                date: dayjs(action.createdAt).format("MM/DD/YYYY"),
                action: action.text,
              };
            })
          : [],

      // related
      related:
        data && complaintIboNumber
          ? data.filter(
              (incident) =>
                incident.complaintAgainst.iboNumber === complaintIboNumber &&
                incident._id !== incidentNumber
            )
          : [],
    });
  }, [
    incident,
    isLoading,
    iboData,
    complaintIbo,
    complaintIsLoading,
    incidentDetails,
    iboNum,
    complaintIboNumber,
    data,
    incidentNumber,
  ]);

  useEffect(() => {
    if (
      incidentInfo &&
      incidentInfo.case &&
      incidentInfo.case.pendingSuspension &&
      incidentInfo.case.pendingSuspension.platforms &&
      incidentInfo.case.pendingSuspension.platforms.length > 0
    ) {
      setPlatforms(incidentInfo.case.pendingSuspension.platforms);

      const isSuspended = incidentInfo.case.pendingSuspension.platforms.some(
        (platform) => platform.status === "suspended"
      );

      setIsSuspended(isSuspended);
    }
  }, [incidentInfo]);

  const refetchPage = () => {
    refetch();
    refetchIncidents();
    refetchIBO();
    refetchComplaint();
  };

  useEffect(() => {
    refetchPage();
  }, []);

  const eventModalRef = useRef();
  const [selectedDate, setSelectedDate] = useState();
  useEffect(() => {
    if (createEventModalOpen && eventModalRef.current) {
      const caseNum = eventModalRef.current.querySelector(
        "input[name='caseNumber']"
      );
      const date = eventModalRef.current.querySelector(
        "[aria-label='Date Select'] > div span"
      );

      if (caseNum) {
        caseNum.value = incidentNumber;
        caseNum.disabled = true;
      }
      if (date && dayjs.isDayjs(selectedDate)) {
        date.textContent = selectedDate.format("MMMM D, YYYY");
      }
    }
  }, [createEventModalOpen, incidentNumber, selectedDate]);

  const convertToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      let fileReader = new FileReader();
      fileReader.onload = function () {
        return resolve({
          data: fileReader.result,
          name: file.name,
          mimetype: file.type,
        });
      };
      fileReader.readAsDataURL(file);
    });
  };

  // submit note
  const handleNoteSubmit = async (e) => {
    // get base64data
    var files = await Promise.all(
      e.files.map((f) => {
        return convertToBase64(f);
      })
    );

    var noteObject = {
      id: incidentNumber,
      title: e.title,
      text: e.text,
      files: files,
    };

    incidentNotePost.mutateAsync(noteObject).then(() => {
      refetchPage();
      setEscalateModalOpen(false);
    });
  };

  // submit communication
  const handleCommunicationSubmit = (e) => {
    var communicationObject = {
      id: incidentNumber,
      sendTo: e.sendTo.includes("Primary Contact")
        ? "primary"
        : e.sendTo.includes("Reported Contact")
        ? "reported"
        : e.sendTo.includes("System Administrator")
        ? "administrator"
        : null,
      template:
        e.template === "Disciplinary Letter - Physical Threat"
          ? "Disciplinary Letter - Physical Therapist"
          : e.template,
      subject: e.subject,
      emailBody: e.emailBody,
    };

    incidentCommunicationPost.mutateAsync(communicationObject).then(() => {
      refetchPage();
    });
  };

  async function handleSuspendUser(item) {
    await suspendUser(item);
    setSuspendModalOpen(false);
  }

  // escalation
  const handleEscalate = (reason, actions) => {
    let escalateObj = {
      id: incidentNumber,
      reason: reason,
      actionsNeeded: actions,
    };

    incidentEscalatePut.mutateAsync(escalateObj).then(() => {
      refetchPage();
    });
  };

  // edit incident
  const handleEditIncident = (e) => {
    let editIncidentObj = {
      id: incidentNumber,
      status: e.status,
      problemType: e.type,
      caseOwner: e.caseOwner,
      severity: e.severity,
    };

    incidentEditPut.mutateAsync(editIncidentObj).then(() => {
      refetchPage();
    });
  };

  // reminder
  const handleReminder = (e) => {
    let reminderObj = {
      id: incidentNumber,
      reason: e.title,
      notes: e.notes,
      reminderDays: e.reminderDays,
    };

    incidentReminderPut.mutateAsync(reminderObj).then(() => {
      refetchPage();
    });
  };

  // doc submission
  const handleDocumentSubmit = async (e) => {
    // get base64data
    var files = await Promise.all(
      e.files.map((f) => {
        return convertToBase64(f);
      })
    );

    let documentObject = {
      id: incidentNumber,
      description: e.description,
      ...(files.length > 0 && { evidence: files }),
    };

    incidentDocumentationPut.mutateAsync(documentObject).then(() => {
      refetchPage();
    });
  };

  // doc download
  const handleDownload = async (e) => {
    console.log(e);
    await downloadImage(e.downloadPath);
  };

  const handleViewModalOpen = (index) => {
    setEmailContents(incidentInfo.communications[index]);
    setViewModalOpen(true);
  };

  // Get an array of all event dates
  const eventDates = testEventsDate.map((event) => {
    const [day, month] = event.date.split(" ");
    const year = new Date().getFullYear();
    return new Date(`${month} ${day}, ${year}`);
  });

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.select}>
          <Select
            options={[
              "Edit Case Info",
              "Escalate",
              "Suspend User",
              "Export Report",
              "Add Reminder",
            ]}
            disabled={isLoading}
            onChange={handleActionsSelect}
            placeholder={"Actions"}
            dropdownRight={true}
            className={styles.selectButton}
          />
        </div>

        <h1 className={styles.header}>Incident #{incidentNumber}</h1>
      </div>
      {isSuspended && <SuspensionBanner suspensionInfo={platforms} />}
      {!isLoading && !isIncidentLoading && !isIncidentsLoading ? (
        <>
          <CaseInfoCard caseInfo={incidentInfo.case} />
          <br />
          <IBOInfoCard ibo={incidentInfo.victim} />
          <br />
          <div className={styles.tabsAndHistoryCardContainer}>
            <TabContainer
              value="Details"
              minHeight="510px"
              className={styles.tabContainer}
              onTabChange={refetchPage}
              children={[
                <Tab title="Details" value="Details">
                  <IncidentDetails details={incidentInfo.aggressor} />
                </Tab>,
                <Tab title="Documentation" value="Documentation">
                  <IncidentDocumentation
                    description={incidentInfo.documentation.description}
                    documentedEvidence={incidentInfo.documentation.evidence}
                    onSubmit={handleDocumentSubmit}
                    onDownload={handleDownload}
                  />
                </Tab>,
                <Tab title="Notes" value="Notes">
                  <CreateNote
                    notes={incidentInfo.notes}
                    onSubmit={handleNoteSubmit}
                    key={id}
                    onImageClick={handleDownload}
                  />
                </Tab>,
                <Tab title="Communication" value="Communication">
                  <IncidentCommunication
                    communications={incidentInfo.communications}
                    caseInfo={incidentInfo}
                    openModal={handleViewModalOpen}
                    onSubmit={handleCommunicationSubmit}
                  />
                </Tab>,
                <Tab title="Related" value="Related">
                  {!isIncidentFetching ? (
                    <RelatedIncidentsList
                      incidents={incidentInfo.related.map((item) => {
                        return {
                          number: item.id ? item.id : "N/A",
                          status: item.status ? item.status : "N/A",
                          type: item.problemType ? item.problemType : "N/A",
                          severity: item.severity ? item.severity : "N/A",
                          dateCreated: item.createdAt
                            ? dayjs(item.createdAt).format("MM/DD/YY")
                            : "N/A",
                        };
                      })}
                      onClick={() => {
                        setTimeout(() => {
                          refetchPage();
                        }, 100);
                      }}
                    />
                  ) : (
                    <div className={styles.loadingTab}>
                      <LoadingPage />
                    </div>
                  )}
                </Tab>,
                <Tab title="Calendar" value="Calendar">
                  <div className={styles.calendarContainer}>
                    <div className={styles.calendar}>
                      <CalendarEventDates
                        allowPastDate={true}
                        eventsDate={eventDates}
                      />
                    </div>

                    <div className={styles.upcoming}>
                      <IncidentUpcomingEvent
                        onClick={() => {
                          setCreateEventModalOpen(true);
                        }}
                        eventsArray={testEventsDate}
                      />
                    </div>
                  </div>
                </Tab>,
              ]}
            />
            <div />

            <div className={styles.history}>
              <HistoryCard history={incidentInfo.history} />
            </div>
          </div>
          <br />
          <div className={styles.button} onClick={() => history.goBack()}>
            Back
          </div>

          {editModalOpen && (
            <EditIncidentInfoModal
              isOpen={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              onSave={(e) => handleEditIncident(e)}
              onSend={(e) => handleCommunicationSubmit(e)}
              editInfo={incidentInfo}
            />
          )}
          {reminderModalOpen && (
            <CreateNewReminderModal
              isOpen={reminderModalOpen}
              onClose={() => setReminderModalOpen(false)}
              onCreate={(e) => handleReminder(e)}
            />
          )}
          {escalateModalOpen && (
            <EscalateCaseModal
              isOpen={escalateModalOpen}
              onClose={() => setEscalateModalOpen(false)}
              onSend={(reason, actions) => handleEscalate(reason, actions)}
            />
          )}
          {suspendModalOpen && (
            <SuspendUserModal
              userInfo={incidentInfo}
              isOpen={suspendModalOpen}
              onClose={() => setSuspendModalOpen(false)}
              onConfirm={handleSuspendUser}
            />
          )}
          {createEventModalOpen && (
            <div className={styles.calendarModalContainer} ref={eventModalRef}>
              <CreateEventModal
                isOpen={createEventModalOpen}
                onClose={() => setCreateEventModalOpen(false)}
                onCreate={handleCreateEvent}
              />
            </div>
          )}
          {viewModalOpen && (
            <IncidentCommunicationModal
              isOpen={viewModalOpen}
              emailInfo={emailContents}
              onView={() => {}}
              onClose={() => {
                setViewModalOpen(false);
              }}
            />
          )}
        </>
      ) : (
        <div className={styles.loading}>
          <LoadingPage />
        </div>
      )}
    </div>
  );
}
